﻿import {$, $$, clickOnEnterKey, get, getDataJson, getDataText, postDataText, show} from "@/Utils"
import {initGoogleMap} from "@/StoreMap"
import {AddressInfoMinimal, ConfirmTradeIn, ReplaceProduct} from "@/interfaces"
import {DataAttributes, TradeInStep} from "@/enums"
import {initCarousels} from "@/sharedComponents/Carousel/Carousel"

const calcContainerId = "calcContainer";
let products: ReplaceProduct[] = [];
let product: ReplaceProduct;

function updateCalcHtml(html: string, calcContentElement: HTMLElement) {
    let ok = isOk(html, calcContentElement)
    if (!ok)
        return;
    if (calcContentElement) {
        const id = calcContentElement.id
        calcContentElement.parentElement.innerHTML = html;
        const calculatorHasId = id && id != "calc-";
        if (calculatorHasId) {
            // Select stale element again.
            calcContentElement = get(id)
            calcContentElement?.scrollIntoView({ behavior: "smooth", block: "end" }); // If canceled then there is nothing to scroll to.
        }
    } else {
        console.error(`Expected element with ID ${calcContainerId} is missing!`);
    }
}

function getClosestOrderProductId(target: HTMLElement) {
    return Number((target.closest("[data-orderproductid]") as HTMLDivElement)?.dataset.orderproductid)
}

function isOk(response: Response, calcContentElement: HTMLElement) {
    if (typeof(response) === "string") {
        try {
            response = JSON.parse(response) as Response;
        } catch {
            return true; // parsing failed. Probably valid html.
        }
    } 
    const msg = (response as any)?.message as string | undefined;
    if (msg) {
        const container = calcContentElement.querySelector(".replace-product-search") || calcContentElement;
        if (container) {
            const div = document.createElement("div");
            div.innerHTML = msg;
            container.appendChild(div);
        }
        return false;
    }
    return true;
}

function initSearch(calcContentElement) {
    const processResponse = (responseProducts: ReplaceProduct[], calcContentElement) => {
        let ok = isOk(responseProducts, calcContentElement)
        if (!ok) 
            return;
        products = responseProducts;
        const formatProduct = (p: ReplaceProduct) =>
            `<li>
            <button type='button' class='autocomplete-list__result'
                data-productid="${p.id}">
                ${p.productName}
            </button/>
            </li>`;

        const autocompleteList = calcContentElement.querySelector(".autocomplete-list");
        autocompleteList.innerHTML = products
            .map(formatProduct)
            .reduce((acc, curr) => acc + curr, "");
    }

    const queryProducts = (searchTerm: string) => {
        const url = `${window.config.tradeIn.getProductsUrl}?searchTerm=${searchTerm}`;
        getDataJson(url, (responseProducts)  => processResponse(responseProducts, calcContentElement));
    }

    const clearResults = () => $(".autocomplete-list").innerHTML = "";

    const autocompleteClickListener = (e: Event) => {
        const target = e.target as HTMLElement;
        const pid = parseInt(target.getAttribute("data-productid"));
        if (isNaN(pid)) return;

        product = products.find(x => x.id === pid);
        product.relatedCartItem = getClosestOrderProductId(target);
        postDataText(window.config.tradeIn.evaluationUrl, product, (e) =>  updateCalcHtml(e, calcContentElement));
    }

    let timeout: number = null;
    const inputListener = (e: Event) => {
        window.clearTimeout(timeout);
        const input = e.currentTarget as HTMLInputElement;
        const searchTerm = input.value;

        if (searchTerm.length < 3) {
            return clearResults();
        }

        timeout = window.setTimeout(() => queryProducts(searchTerm), 750);
    }
    calcContentElement.querySelector(".searchProducts")?.addDataBoundEventListener("input", inputListener, "search-input");
    calcContentElement.querySelector(".autocomplete-list")?.addDataBoundEventListener("click", autocompleteClickListener, "autocomplete-list-click");
}

function initEvaluation(calcContentElement: HTMLElement) {
    const handler = (e: Event) => {
        const target = e.target as HTMLElement;
        const radio = target.previousElementSibling as HTMLInputElement;
        radio.checked = true;

        const container = target.closest("[data-question-container]");
        const nextContainer = container.nextElementSibling as HTMLElement;
        if (nextContainer) {
            nextContainer.classList.remove("disabled-question");
        } else {
            const nextButton = calcContentElement.querySelector(".trade-in-next") as HTMLElement;
            show(nextButton);
        }
    }

    calcContentElement.querySelectorAll(".replace-answers span").forEach(x => {
        x.addDataBoundEventListener("click", handler)
        x.addDataBoundEventListener("keydown", (e: KeyboardEvent) => {
            clickOnEnterKey(e, x as HTMLElement)
        }, "enter-keypress-bound")
    });
}

function initSummary(calcContentElement: HTMLElement) {
    const confirmBtn = calcContentElement.querySelector('.trade-in-next') as HTMLElement;
    if (!confirmBtn) return;

    const requiredInputs = Array.from(
        calcContentElement.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
            '.imei, .bank-account, .trade-in-confirm-checkbox, .trade-in-confirm-locks, ' +
            '.trade-in-confirm-campaign-terms, .input-address_search, #AddressInfo_PostalCode'
        )
    );

    if (requiredInputs.length === 0) return;

    const isInputValid = (el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => {
        // Special rule: address search field is optional if manual address AND postal code are filled
        if ((el as HTMLElement).classList?.contains('input-address_search')) {
            const manualAddress = document.querySelector<HTMLInputElement>('#AddressInfo_Address');
            const manualPostal = document.querySelector<HTMLInputElement>('#AddressInfo_PostalCode');
            const manualOk = !!manualAddress?.value?.trim() && !!manualPostal?.value?.trim();
            if (manualOk) return true;
            // else fall through to default validation for the search field itself
        }
        const type = (el as HTMLInputElement).type;
        if (type === 'checkbox') {
            return (el as HTMLInputElement).checked;
        }
        if (type === 'radio') {
            // For radios, ensure some radio with the same name is checked
            const name = (el as HTMLInputElement).name;
            const group = calcContentElement.querySelectorAll<HTMLInputElement>(`input[type="radio"][name="${CSS.escape(name)}"]`);
            return Array.from(group).some(r => r.checked);
        }
        // For text-like inputs, require non-empty value; honor pattern if provided
        if ('value' in el) {
            const value = (el as HTMLInputElement).value?.trim();
            if (!value) return false;
            const pattern = (el as HTMLInputElement).getAttribute('pattern');
            if (pattern) {
                try {
                    const re = new RegExp(`^${pattern}$`);
                    return re.test(value);
                } catch {
                    // If the pattern is invalid, fall back to non-empty
                    return true;
                }
            }
            return true;
        }
        return true;
    };

    const validate = () => {
        const allOk = requiredInputs.every(isInputValid);
        if (allOk) {
            show(confirmBtn);
            confirmBtn.classList.remove('d-none');
        } else {
            confirmBtn.classList.add('d-none');
        }
    };

    // Hide initially until all confirmations are satisfied
    confirmBtn.classList.add('d-none');

    // Listen for changes on required inputs
    requiredInputs.forEach(inp => {
        (inp as HTMLInputElement).addDataBoundEventListener('change', validate);
        (inp as HTMLInputElement).addDataBoundEventListener('input', validate);
    });

    // Also listen to manual address fields to re-validate when they change,
    // so the address search field can become optional dynamically
    const manualAddress = document.querySelector<HTMLInputElement>('#AddressInfo_Address');
    const manualPostal = document.querySelector<HTMLInputElement>('#AddressInfo_PostalCode');
    manualAddress?.addDataBoundEventListener('input', validate);
    manualAddress?.addDataBoundEventListener('change', validate);
    manualPostal?.addDataBoundEventListener('input', validate);
    manualPostal?.addDataBoundEventListener('change', validate);

    // Initial evaluation
    validate();
}

function getProduct(calcContentElement: HTMLElement) {
    const answers: number[] = [];
    const answerTexts: string[] = [];
    const questions: string[] = [];
    const questionLabels: string[] = [];

    calcContentElement.querySelectorAll("[data-question-container]").forEach(x => {
        const input = x.querySelector(".replace-answer:checked") as HTMLInputElement;
        answers.push(parseInt(input.value));

        const answerText = input.nextElementSibling as HTMLElement;
        answerTexts.push(answerText.textContent);

        const question = x.querySelector(".replace-question") as HTMLElement;
        questions.push(question.textContent);
        questionLabels.push(question.getAttribute("data-label"));
    });

    product.answers = answers;
    product.answerTexts = answerTexts;
    product.questions = questions;
    product.questionLabels = questionLabels;
}

function initNav(step: TradeInStep, calcContentElement: HTMLElement) {
    if (step === TradeInStep.Search) return;

    const back = calcContentElement.querySelector(".trade-in-back") as HTMLElement;
    const next = calcContentElement.querySelector(".trade-in-next") as HTMLElement;
    const cancelBtn = calcContentElement.querySelector(".trade-in-cancel") as HTMLElement;
    let backClick: Function = null;

    if (step === TradeInStep.Evaluation) {

        backClick = () => getDataText(window.config.tradeIn.searchUrl, (e)  => updateCalcHtml(e, calcContentElement));
        const nextClick = () => {
            getProduct(calcContentElement);
            postDataText(window.config.tradeIn.summaryUrl, product, (e) => { updateCalcHtml(e, calcContentElement); });

        }
        next.addDataBoundEventListener("click", nextClick);
    }
    if (step === TradeInStep.Summary) {
   
        backClick = function (e) {
            // Refreshed summary and went back to evaluation.
            if (product == undefined) {
                product = {
                    id: 0,
                    productName: "",
                    relatedCartItem: getClosestOrderProductId(e.target as HTMLElement),
                    answers: [],
                    answerTexts: [],
                    questions: [],
                    questionLabels: []
                } as ReplaceProduct;
            }
            return postDataText(window.config.tradeIn.evaluationUrl, product, (e) => updateCalcHtml(e, calcContentElement));
        };
        const nextClick = (e) => {
            const id = getClosestOrderProductId(e.target as HTMLElement);
            const bankAccountId = calcContentElement.querySelector(".bank-account") as HTMLInputElement;
            const imei = calcContentElement.querySelector(".imei") as HTMLInputElement;

            let confirmModel: ConfirmTradeIn = {
                orderProductId: id,
                bankAccountNumber: bankAccountId.value,
                imei: imei.value,
            } as ConfirmTradeIn;
            confirmModel.addressInfo = {
                address: (calcContentElement.querySelector("#AddressInfo_Address") as HTMLInputElement).value,
                city: (calcContentElement.querySelector("#AddressInfo_City") as HTMLInputElement).value,
                county: (calcContentElement.querySelector("#AddressInfo_County") as HTMLInputElement).value,
                postalCode: (calcContentElement.querySelector("#AddressInfo_PostalCode") as HTMLInputElement).value,
            } as AddressInfoMinimal
            if (calcContentElement.querySelector("#AddressInfo_Settlement")){
                confirmModel.addressInfo.settlement = (calcContentElement.querySelector("#AddressInfo_Settlement" )as HTMLInputElement).value
            }
            postDataText(window.config.tradeIn.confirmUrl, confirmModel, (e) => updateCalcHtml(e, calcContentElement));
        }     
        const cancelClick = (e) => {
            const id = getClosestOrderProductId(e.target as HTMLElement);
            postDataText(window.config.tradeIn.cancelUrl, id, (e) => updateCalcHtml(e, calcContentElement));
        }  
        
        if (cancelBtn) {
            cancelBtn?.addDataBoundEventListener("click", cancelClick);
        } else {
            next?.addDataBoundEventListener("click", nextClick);
        }
    }

    back?.addDataBoundEventListener("click", backClick);
}

function init() {
    const calcContentElement = $$(".calc-content");
    calcContentElement.forEach(calcContentElement => {
            const step = parseInt(calcContentElement.getAttribute(DataAttributes.Step)) as TradeInStep;
            switch (step) {
                case TradeInStep.Search:
                    initSearch(calcContentElement);
                    break;
                case TradeInStep.Evaluation:
                    initEvaluation(calcContentElement);
                    break;
                case TradeInStep.Summary:
                    initSummary(calcContentElement);
                    break;
            }

            initNav(step, calcContentElement)
        }
    );

}

function initTradeIn() {

    initGoogleMap();

    const mapContainerId = "mapContainer"
    const map = get(mapContainerId);
    if (map) {
        if (!map.querySelector(".map-content")) {
            map.innerHTML = "";
            map.append($(".map-content"));
        }
    } else {
        console.error(`Expected element with ID ${mapContainerId} is missing!`);
    }
    // Only one calculator per page.
    const calc = get(calcContainerId);
    if (calc) {
        if (!calc.querySelector(".calc-content")) {
            calc.innerHTML = "";
            calc.append($(".calc-content"));
        }
    } else {
        console.error(`Expected element with ID ${calcContainerId} is missing!`);
    }
    // This supports multiple trade ins per page.
    init();
}



function initializeTradingAndCarouselComponents() {
    initTradeIn();
    initCarousels();
}

window.addEventListener("afterLoadDone", () => {
    // When user removes item from the cart, we need to rebind search input.
    initializeTradingAndCarouselComponents();
})
window.addEventListener("load", () => {
    initializeTradingAndCarouselComponents();
});
